import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { ShoppingBag, TrendingUp, Gift, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchProducts, fetchSiteSettings } from '@/lib/firebase';
import { fetchProductsAdmin, fetchSiteSettingsAdmin } from '@/lib/firebaseAdmin';
import SeoHead from '@/components/SeoHead';
import ProductCard from '@/components/ProductCard';
import EditorialHeroSection from '@/components/sections/EditorialHeroSection';
import { getAbsoluteUrl } from '@/lib/seo';
import { productPropType, heroContentPropType } from '@/lib/propTypes';
import {
  buildCatalogPath,
  getProductDepartment,
  getProductSubdivision,
} from '@/lib/productRoutes';

const getCryptoApi = () => globalThis.crypto || globalThis.msCrypto;

const getSecureRandomInt = (maxExclusive) => {
  if (maxExclusive <= 1) return 0;

  const cryptoApi = getCryptoApi();
  if (!cryptoApi?.getRandomValues) return 0;

  const buffer = new Uint32Array(1);
  const maxUint32 = 0x100000000;
  const unbiasedLimit = maxUint32 - (maxUint32 % maxExclusive);
  let randomValue = 0;

  do {
    cryptoApi.getRandomValues(buffer);
    randomValue = buffer[0];
  } while (randomValue >= unbiasedLimit);

  return randomValue % maxExclusive;
};

const shuffleArray = (array) => {
  const cryptoApi = getCryptoApi();
  if (!cryptoApi?.getRandomValues) return [...array];

  const shuffled = [...array];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = getSecureRandomInt(index + 1);
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

const categories = [
  {
    name: 'Women',
    department: 'Women',
    description: 'Heels, wedges, sandals, sneakers, brogues, and flats.',
  },
  {
    name: 'Men',
    department: 'Men',
    description: 'Casuals, sneakers, and officials.',
  },
  {
    name: 'Kids',
    department: 'Kids',
    description: 'Daily pairs for school and play.',
  },
  {
    name: 'Workwear',
    department: 'Men',
    subcategory: 'Officials',
    imageSubcategory: 'Officials',
    description: 'Smart, comfortable styles for the working day.',
  },
];

const benefits = [
  { icon: Truck, title: 'Fast Delivery', description: 'Quick and reliable shipping across Kenya' },
  { icon: TrendingUp, title: 'Trendy Collection', description: 'Latest styles updated regularly' },
  { icon: Gift, title: 'Great Prices', description: 'Quality footwear at affordable rates' },
  { icon: ShoppingBag, title: 'Easy Returns', description: '3-day hassle-free returns' },
];

const getProductCreatedAt = (product) => {
  const createdAt = Number(product?.createdAt);
  if (Number.isFinite(createdAt) && createdAt > 0) return createdAt;

  const updatedAt = Number(product?.updatedAt);
  if (Number.isFinite(updatedAt) && updatedAt > 0) return updatedAt;

  return 0;
};

const buildHomePageData = (products = [], siteSettings = { homeHeroContent: null }) => {
  const featured = [...products]
    .sort((left, right) => getProductCreatedAt(right) - getProductCreatedAt(left))
    .slice(0, 3);

  const categoryImages = categories.reduce((acc, category) => {
    const categoryProducts = products.filter(
      (product) => getProductDepartment(product) === category.department && product.image
    );
    const match =
      categoryProducts.find(
        (product) =>
          category.imageSubcategory &&
          getProductSubdivision(product) === category.imageSubcategory
      ) || categoryProducts[0];
    acc[category.name] = match?.image || '';
    return acc;
  }, {});

  const selectedProducts = (siteSettings.heroProductIds || [])
    .map((id) => products.find((product) => product.id === id))
    .filter(Boolean);

  const remainingProducts = products.filter(
    (product) => !selectedProducts.some((selected) => selected.id === product.id)
  );
  const sourceProducts = selectedProducts.length
    ? [...selectedProducts, ...shuffleArray(remainingProducts)].slice(0, 8)
    : shuffleArray(products).slice(0, 8);
  const heroProducts = shuffleArray(
    sourceProducts
      .map((product) => ({
        id: product.id,
        name: product.name,
        category: getProductDepartment(product),
        image: product.image,
        price: product.price,
      }))
      .filter(
        (slide, index, slides) =>
          slide.image && slides.findIndex((item) => item.image === slide.image) === index
      )
  ).slice(0, 8);

  return {
    featured,
    heroProducts,
    siteSettings,
    categoryImages,
  };
};

export default function Home({
  featured = [],
  heroProducts = [],
  siteSettings = { homeHeroContent: null },
  categoryImages = {},
}) {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [homeData, setHomeData] = useState({
    featured,
    heroProducts,
    siteSettings,
    categoryImages,
  });

  useEffect(() => {
    setHomeData({
      featured,
      heroProducts,
      siteSettings,
      categoryImages,
    });
  }, [featured, heroProducts, siteSettings, categoryImages]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetchProducts({ forceFresh: true }),
      fetchSiteSettings({ forceFresh: true }),
    ])
      .then(([freshProducts, freshSiteSettings]) => {
        if (!isMounted) return;

        setHomeData((currentHomeData) =>
          buildHomePageData(
            freshProducts || [],
            freshSiteSettings || currentHomeData.siteSettings || { homeHeroContent: null }
          )
        );
      })
      .catch((error) => {
        console.warn('Could not refresh homepage products on the client:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleFeatured = homeData.featured || [];
  const visibleHeroProducts = homeData.heroProducts || [];
  const visibleSiteSettings = homeData.siteSettings || { homeHeroContent: null };
  const visibleCategoryImages = homeData.categoryImages || {};

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error('Please enter your email address.');
      return;
    }

    setIsSubscribing(true);

    const browserWindow = globalThis.window;

    if (browserWindow?.localStorage) {
      const existingSubscribers = JSON.parse(
        browserWindow.localStorage.getItem('newsletterSubscribers') || '[]'
      );

      if (!existingSubscribers.includes(normalizedEmail)) {
        browserWindow.localStorage.setItem(
          'newsletterSubscribers',
          JSON.stringify([...existingSubscribers, normalizedEmail])
        );
      }
    }

    setEmail('');
    toast.success('Thanks for subscribing. You are on the list!');
    setIsSubscribing(false);
  };

  return (
    <main className="overflow-hidden bg-white">
      <SeoHead
        title="Trendy Quest Collection | Men, Women, Kids and Workwear in Kenya"
        description="Shop men's, women's and kids' footwear, delivered across Kenya."
        path="/"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': getAbsoluteUrl('/'),
          url: getAbsoluteUrl('/'),
          name: 'Trendy Quest Collection | Men, Women, Kids and Workwear in Kenya',
          description: "Shop men's, women's and kids' footwear, delivered across Kenya.",
          primaryImageOfPage: getAbsoluteUrl('/homepage banner.jpg') || undefined,
          about: {
            '@id': getAbsoluteUrl('/#store'),
          },
          isPartOf: {
            '@type': 'WebSite',
            '@id': getAbsoluteUrl('/#website'),
            url: getAbsoluteUrl('/'),
            name: 'Trendy Quest Collection',
          },
        }}
      />

      <EditorialHeroSection
        products={visibleHeroProducts}
        homeHeroContent={visibleSiteSettings.homeHeroContent}
      />

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="px-4 py-10 sm:py-16"
      >
        <div className="w-full">
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {categories.map((category, index) => (
              <Link
                key={category.name}
                href={buildCatalogPath({
                  department: category.department,
                  subcategory: category.subcategory,
                })}
                className="block min-w-0"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -3 }}
                  className="group relative flex aspect-[4/3] cursor-pointer items-end overflow-hidden rounded-2xl bg-[#f6f3ee] p-3 shadow-[0_14px_34px_rgba(17,17,17,0.08)] transition-shadow duration-300 hover:shadow-[0_26px_60px_rgba(17,17,17,0.16)] sm:rounded-3xl sm:p-5"
                >
                  <Image
                    src={visibleCategoryImages[category.name] || '/homepage banner.jpg'}
                    alt={`${category.name} footwear`}
                    fill
                    sizes="(max-width: 640px) 50vw, 25vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

                  <div className="relative z-10">
                    <h2 className="text-xl font-playfair font-bold text-white drop-shadow sm:text-3xl">
                      {category.name}
                    </h2>
                    <p className="mt-1 hidden text-sm leading-5 text-white/90 sm:block">
                      {category.description}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gray-50 px-4 py-14 sm:py-20"
      >
        <div className="w-full">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-10 flex flex-col gap-4 sm:mb-12 md:flex-row md:items-end md:justify-between"
          >
            <div>
              <h2 className="mb-2 text-3xl font-playfair font-bold text-gray-900 sm:text-4xl md:text-5xl">
                New Arrivals
              </h2>
              <p className="text-base text-gray-600 sm:text-lg">Latest styles just added</p>
            </div>
            <Link
              href="/products"
              className="inline-flex items-center gap-2 self-start font-bold text-[#13833A] transition-all hover:gap-3 md:self-auto md:text-lg"
            >
              View All {'\u2192'}
            </Link>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:grid-cols-4"
          >
            {visibleFeatured.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="px-4 py-14 sm:py-20"
      >
        <div className="w-full">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-4 text-center text-3xl font-playfair font-bold text-gray-900 sm:text-4xl md:text-5xl"
          >
            Why Choose Us
          </motion.h2>
          <p className="mb-12 text-center text-base text-gray-600 sm:mb-16 sm:text-lg">
            Exceptional quality and service in every purchase
          </p>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="rounded-2xl border border-[#d9d6cf] bg-[#fcfaf6] p-6 text-center transition-all hover:border-[#c9c3b9] hover:bg-white"
              >
                <benefit.icon className="mx-auto mb-4 h-12 w-12 text-[#13833A]" />
                <h3 className="mb-2 text-xl font-playfair font-bold text-gray-900">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-[#fcfaf6] px-4 py-14 sm:py-18"
      >
        <div className="mx-auto w-full max-w-5xl rounded-[1.35rem] border border-[#e7dcc8] bg-white px-4 py-6 shadow-[0_18px_50px_rgba(17,17,17,0.05)] sm:rounded-[2rem] sm:px-8 sm:py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">
            About Trendy Quest
          </p>
          <h2 className="mt-3 text-3xl font-playfair font-bold text-[#182218] sm:text-4xl">
            Footwear and clothing picked for everyday wear in Kenya
          </h2>
          <div className="mt-5 space-y-4 text-sm leading-7 text-[#5d625b] sm:text-base">
            <p>
              Trendy Quest Collection is a Nairobi-based store focused on practical, stylish
              footwear and easy-to-wear clothing for men, women, kids, and everyday shopping
              across Kenya.
            </p>
            <p>
              We stock casual pairs, sandals, sneakers, officials, kids styles, tracksuits, and
              sweater layers, then help customers order quickly through the website or WhatsApp
              with fast delivery support.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-[#13833A] via-[#1a5f42] to-[#0d5a27] px-4 py-14 sm:py-20"
      >
        <div className="w-full text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-4 text-3xl font-playfair font-bold text-white sm:text-4xl md:text-5xl"
          >
            Stay Updated
          </motion.h2>
          <p className="mb-8 text-base text-white/90 sm:text-lg md:text-xl">
            Join our WhatsApp channel for more timely updates and awesome offers.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mx-auto flex max-w-md flex-col gap-3 md:flex-row"
          >
            <a
              href="https://whatsapp.com/channel/0029VbD27YVGZNChknNrd81k"
              target="_blank"
              rel="noreferrer"
              className="w-full inline-flex items-center justify-center rounded-full bg-white px-6 py-3 font-bold text-[#13833A] transition-all hover:bg-[#f3f3f3] sm:w-auto sm:px-8 sm:py-4"
            >
              Join Now
            </a>
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gray-50 px-4 py-14 sm:py-20"
      >
        <div className="w-full text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-4 text-3xl font-playfair font-bold text-gray-900 sm:text-4xl md:text-5xl"
          >
            Ready to Step Up Your Style?
          </motion.h2>
          <p className="mb-8 text-base text-gray-600 sm:text-lg md:text-xl">
            Discover premium footwear that combines comfort, style, and quality
          </p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              href="/products"
              className="inline-flex w-full items-center justify-center rounded-full bg-[#13833A] px-5 py-3 text-base font-bold text-white transition-all hover:bg-[#0d5a27] sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
            >
              Shop Now
            </Link>
            <Link
              href="/contact"
              className="inline-flex w-full items-center justify-center rounded-full border-2 border-[#13833A] px-5 py-3 text-base font-bold text-[#13833A] transition-all hover:bg-[#13833A] hover:text-white sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </motion.section>
    </main>
  );
}

Home.propTypes = {
  featured: PropTypes.arrayOf(productPropType),
  heroProducts: PropTypes.arrayOf(productPropType),
  siteSettings: PropTypes.shape({
    homeHeroContent: heroContentPropType,
  }),
  categoryImages: PropTypes.objectOf(PropTypes.string),
};

export async function getStaticProps() {
  const [products, siteSettings] = await Promise.all([
    fetchProductsAdmin(),
    fetchSiteSettingsAdmin(),
  ]);

  return {
    props: buildHomePageData(products, siteSettings),
    revalidate: 60,
  };
}
